/**
 * Message builders — convenience encoders for the client→server messages a test harness
 * (or the DO) needs to send. Thin wrappers over xmsg.ts/netmessage.ts encode() that match
 * the C++ XMsg*::ToBuffer signatures.
 */
import { encode } from './xmsg.ts';
import { encodeGame } from './netmessage.ts';
import { encodeInner } from './gamemsg.ts';
import { ByteWriter } from './bytebuffer.ts';
import { XMSG, NMID, PROTOCOL_CURRENT, CLIENT_LOADTEST } from './constants.ts';

export const handshake = (clientId = CLIENT_LOADTEST, protocol = PROTOCOL_CURRENT) =>
  encode({ id: XMSG.HANDSHAKE, kind: 'dword2', dw0: clientId, dw1: protocol });

export const login = (username = '', token = '', did = '', platform = '') =>
  encode({ id: XMSG.LOGIN, kind: 'strings', strings: [username, token, did, platform] });

export const lobbyJoin = () => encode({ id: XMSG.LOBBYJOIN, kind: 'zero' });

export const roomJoin = (roomid: number, password = '') =>
  encode({ id: XMSG.ROOMJOIN, kind: 'dword_string', dw0: roomid, s0: password });

export const gameJoin = (gameid: number, roomid: number) =>
  encode({ id: XMSG.GAMEJOIN, kind: 'dword2', dw0: gameid, dw1: roomid });

export const roomLeave = (dw0 = 0) => encode({ id: XMSG.ROOMLEAVE, kind: 'dword1', dw0 });
export const gameLeave = () => encode({ id: XMSG.GAMELEAVE, kind: 'zero' });

export const roomSendChat = (chat: string) =>
  encode({ id: XMSG.ROOMSENDCHAT, kind: 'strings', strings: [chat] });

export const gameSendChat = (chat: string) =>
  encode({ id: XMSG.GAMESENDCHAT, kind: 'strings', strings: [chat] });

/** ROOMCREATEGAME (0x3b): the 60-byte GameParams payload, verbatim. */
export const roomCreateGame = (gameParamsBytes: Uint8Array) =>
  encode({ id: XMSG.ROOMCREATEGAME, kind: 'raw', payload: gameParamsBytes });

/** CsPlayerJoin (nmid 8): the player name in a fixed char[32] field. */
export const playerJoin = (name: string) => {
  const w = new ByteWriter();
  w.writeFixedString(name, 32);
  return encodeGame({
    id: XMSG.GAMENETMESSAGE,
    kind: 'gamenetmessage',
    nmid: NMID.CsPlayerJoin,
    inner: w.toUint8Array(),
  });
};

/** CsClientCommands (nmid 3): relayed 28-byte Message command blobs. */
export const clientCommands = (blobs: Uint8Array[]) =>
  encodeGame({
    id: XMSG.GAMENETMESSAGE,
    kind: 'gamenetmessage',
    nmid: NMID.CsClientCommands,
    inner: encodeInner({ nmid: NMID.CsClientCommands, kind: 'clientcommands', commands: blobs }),
  });

/** A bare control NetMessage (ClientReady/RequestBegin): wrapped as GAMENETMESSAGE, no payload. */
export const controlNm = (nmid: number) =>
  encodeGame({ id: XMSG.GAMENETMESSAGE, kind: 'gamenetmessage', nmid, inner: new Uint8Array(0) });

export const clientReady = () => controlNm(NMID.CsClientReady);
export const requestBegin = () => controlNm(NMID.CsRequestBeginGame);
export const requestAddBot = () => controlNm(NMID.CsAddBotRequest); // creator asks the DO to add a bot to its game

/** CsRemoveBotRequest (TS-only): the creator asks the DO to remove the bot occupying staging-roster slot
 *  `pid` from its OWN game (the staging roster's selectable bot rows → the ADD button flips to REMOVE). */
export const requestRemoveBot = (pid: number) => {
  const w = new ByteWriter();
  w.writeDword(pid >>> 0);
  return encodeGame({
    id: XMSG.GAMENETMESSAGE,
    kind: 'gamenetmessage',
    nmid: NMID.CsRemoveBotRequest,
    inner: w.toUint8Array(),
  });
};

/** CsResumeGame (TS-only): a reconnected client asks the DO to re-bind it to its held slot in `gameId`
 *  and resume lockstep. `lastBlock` is the last block the client received (its driver block point,
 *  Sim.resumeBlockPoint()) — the DO replays every recorded block AFTER it so the sim fast-forwards to live. */
export const resumeGame = (gameId: number, lastBlock: number) => {
  const w = new ByteWriter();
  w.writeDword(gameId >>> 0);
  w.writeDword(lastBlock >>> 0);
  return encodeGame({
    id: XMSG.GAMENETMESSAGE,
    kind: 'gamenetmessage',
    nmid: NMID.CsResumeGame,
    inner: w.toUint8Array(),
  });
};

export const updateResult = (cUpdatesBlock: number, hash = 0, cmsLatency = 0) =>
  encodeGame({
    id: XMSG.GAMEUPDATERESULT,
    kind: 'gameupdateresult',
    cUpdatesBlock,
    hash,
    cmsLatency,
  });
